import type { LexiconEntry } from './lexicon-schema.js'

/**
 * Melody Trainer timing lexicon - 9 entries, authored by Bowie in the Epic design §10.
 *
 * Every entry maps to exactly one `scoreTiming` verdict. This is the second tool in the suite
 * whose output is a MEASUREMENT OF THE USER, and a surface that assembled its own prose could tell
 * a musician something no one wrote and no one reviewed - so it does not assemble one, and the
 * only sentences a player reads about their timing are these.
 *
 * **The band entries describe distance from the phrase's own mean placement, not from the beat.**
 * The input-latency calibration that would have licensed an absolute claim was removed once it was
 * shown to be measured on a different audio path from the one the reference plays through, so the
 * copy was rewritten to claim only what survives an uncompensated device delay (Bowie,
 * 2026-09-13). Four entries went with it: `timing-rushing`, `timing-dragging` and `timing-even`
 * named a whole-phrase lean the tool can no longer see, and `timing-calibrate-first` /
 * `timing-calibrated` described a gate that no longer exists. `timing-relative-only` is the
 * caveat that replaced them, and it is shown once with every timing verdict.
 *
 * `{{ms}}` is unsigned integer milliseconds - the direction lives in the entry id, not in the sign
 * - and `{{r}}` is the feel-match correlation to two decimals. Both interpolate at the use site
 * through `interpolateTokens`, which strips an unfilled token rather than leaking the raw
 * placeholder.
 *
 * The two cross-link entries live in `lexicon-crosslink-entries.ts` with the rest of the
 * cross-tool semantics.
 *
 * Copy rules applied by the author: beginner never says "ms" or "latency"; intermediate names the
 * number; advanced states the rule. `timing-feel-straight` is never a judgement - playing straight
 * is a choice, and the sentence only reports that the reference leans.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §10
 */
export const TIMING_LEXICON_ENTRIES: LexiconEntry[] = [
  // ───────── Per-note bands (§6.2) ─────────
  {
    id: 'timing-on',
    category: 'timing',
    displayName: 'With the phrase',
    displayNameShort: 'On',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Even with the rest.',
      intermediate: 'Steady — within 25 ms of your own average placement.',
      advanced: '≤ 25 ms from the phrase mean.',
    },
    tagLabel: { beginner: 'on', intermediate: 'on', advanced: 'on' },
  },
  {
    id: 'timing-close-early',
    category: 'timing',
    displayName: 'A touch early',
    displayNameShort: 'Close early',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Slightly ahead of your own pace.',
      intermediate: 'A touch early against your own average — {{ms}} ms.',
      advanced: '−{{ms}} ms from the phrase mean.',
    },
    tagLabel: { beginner: 'a bit early', intermediate: 'early', advanced: '−{{ms}} ms' },
  },
  {
    id: 'timing-close-late',
    category: 'timing',
    displayName: 'A touch late',
    displayNameShort: 'Close late',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Slightly behind your own pace.',
      intermediate: 'A touch late against your own average — {{ms}} ms.',
      advanced: '+{{ms}} ms from the phrase mean.',
    },
    tagLabel: { beginner: 'a bit late', intermediate: 'late', advanced: '+{{ms}} ms' },
  },
  {
    id: 'timing-off-early',
    category: 'timing',
    displayName: 'Early',
    displayNameShort: 'Early',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Ahead of your own pace.',
      intermediate: 'Early against your own average — {{ms}} ms.',
      advanced: '−{{ms}} ms from the phrase mean.',
    },
    tagLabel: { beginner: 'early', intermediate: 'early', advanced: '−{{ms}} ms' },
  },
  {
    id: 'timing-off-late',
    category: 'timing',
    displayName: 'Late',
    displayNameShort: 'Late',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Behind your own pace.',
      intermediate: 'Late against your own average — {{ms}} ms.',
      advanced: '+{{ms}} ms from the phrase mean.',
    },
    tagLabel: { beginner: 'late', intermediate: 'late', advanced: '+{{ms}} ms' },
  },
  {
    id: 'timing-wrong-place',
    category: 'timing',
    displayName: 'Wrong place',
    displayNameShort: 'Displaced',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'That note landed in the wrong place.',
      intermediate: 'Displaced — {{ms}} ms, a different beat position.',
      advanced: '|{{ms}}| > 120 ms: moved to another slot.',
    },
    tagLabel: { beginner: 'wrong place', intermediate: 'displaced', advanced: 'displaced' },
  },

  // ───────── What the bands do and do not claim ─────────
  {
    id: 'timing-relative-only',
    category: 'timing',
    displayName: 'Measured against yourself',
    displayNameShort: 'Relative',
    pedagogicalLevel: 1,
    copy: {
      beginner: 'Timing is measured against your own average, not against the clock.',
      intermediate:
        'Placement is relative to your own average — absolute beat accuracy needs a calibrated setup, which is coming.',
      advanced: 'Errors are mean-centred; absolute onset timing is unavailable until the audio paths agree.',
    },
    tagLabel: { beginner: 'your own pace', intermediate: 'relative', advanced: 'mean-centred' },
  },

  // ───────── Feel match (§6.2) ─────────
  {
    id: 'timing-feel-matched',
    category: 'timing',
    displayName: 'Feel matched',
    displayNameShort: 'Feel',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You caught the feel.',
      intermediate: "You matched the melody's push and pull.",
      advanced: 'Feel match r = {{r}}.',
    },
    tagLabel: { beginner: 'feel', intermediate: 'feel matched', advanced: 'r = {{r}}' },
  },
  {
    id: 'timing-feel-straight',
    category: 'timing',
    displayName: 'Played straight',
    displayNameShort: 'Straight',
    pedagogicalLevel: 2,
    copy: {
      beginner: 'You played it straight — the melody leans a little.',
      intermediate: 'The melody pushes and pulls; you played it on the grid.',
      advanced: 'Feel match r = {{r}}; reference has offsets.',
    },
    tagLabel: { beginner: 'straight', intermediate: 'played straight', advanced: 'r = {{r}}' },
  },

  // ───────── Tool intro ─────────
  {
    id: 'timing-intro',
    category: 'timing',
    displayName: 'Melody Trainer',
    displayNameShort: 'Practice',
    pedagogicalLevel: 1,
    copy: {
      beginner: "Hear it, then sing or play it back — we'll check the notes and the timing.",
      intermediate: 'Reproduce the melody; pitch and timing are scored on every note.',
      advanced: 'Reproduction training: pitch (VocalLib) + timing (onset error vs. the melody with its feel).',
    },
    tagLabel: { beginner: 'practice', intermediate: 'practice', advanced: 'practice' },
  },
]
